import { useState } from 'react';
import { Link } from 'react-router-dom';
import LessonCard from './components/LessonCard';
import { allLessons } from './data';
import SEOHead from '../../../components/SEOHead';

export default function Grade11Page() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'worksheets'>('lessons');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showMembershipUpsell, setShowMembershipUpsell] = useState(false);
  const navigate = window.REACT_APP_NAVIGATE;
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'Grade 11 Algebra 2 & Trigonometry | New York Mathematics | Augustine Anyimadu-Ahenkae',
    description: 'Complete Grade 11 Algebra 2 and Trigonometry curriculum. Master polynomial and rational functions, complex numbers, logarithms, trigonometric identities, and sequences. NY Regents aligned.',
    url: `${siteUrl}/grades/grade-11`,
    provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl },
    educationalLevel: 'Grade 11',
    inLanguage: 'en',
    hasCourseInstance: [
      { '@type': 'CourseInstance', name: 'Polynomial & Rational Functions', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Exponential & Logarithmic Functions', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Trigonometric Functions & Identities', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Complex Numbers & Sequences', courseMode: 'online' },
    ],
  };

  const worksheets = [
    {
      id: 'alg2-1',
      title: 'Polynomial Operations & Factoring',
      description: 'Master polynomial multiplication, division, and advanced factoring techniques including grouping and sum/difference of cubes',
      pages: 8,
      problems: 30,
      difficulty: 'Medium' as const,
      isFree: true
    },
    {
      id: 'alg2-2',
      title: 'Quadratic Functions & Complex Numbers',
      description: 'Complete the square, use the quadratic formula, and perform operations with complex numbers in standard form',
      pages: 9,
      problems: 28,
      difficulty: 'Medium' as const,
      isFree: false
    },
    {
      id: 'alg2-3',
      title: 'Exponential & Logarithmic Functions',
      description: 'Solve exponential equations, apply logarithm properties, and model real-world growth and decay scenarios',
      pages: 10,
      problems: 32,
      difficulty: 'Hard' as const,
      isFree: false
    },
    {
      id: 'alg2-4',
      title: 'Rational Functions & Equations',
      description: 'Simplify rational expressions, solve rational equations, and identify asymptotes and discontinuities',
      pages: 7,
      problems: 25,
      difficulty: 'Hard' as const,
      isFree: false
    },
    {
      id: 'alg2-5',
      title: 'Sequences, Series & Binomial Theorem',
      description: 'Work with arithmetic and geometric sequences, find series sums, and expand binomials using Pascal\'s triangle',
      pages: 8,
      problems: 27,
      difficulty: 'Medium' as const,
      isFree: false
    }
  ];

  const handleWorksheetClick = (worksheet: typeof worksheets[0]) => {
    if (worksheet.isFree) {
      alert('Free sample download started!');
    } else {
      setShowMembershipUpsell(true);
      setTimeout(() => {
        document.getElementById('membership-upsell')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const handleBrowseAllClick = () => {
    window.REACT_APP_NAVIGATE('/worksheets?category=grade-11');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-purple-50">
      <SEOHead
        title="Grade 11 Algebra 2 & Trigonometry New York | NY Regents Prep | Augustine Anyimadu-Ahenkae"
        description="Master Grade 11 Algebra 2 and Trigonometry with comprehensive lessons. Polynomials, complex numbers, logarithms, trigonometric identities, sequences. Full NY Regents exam practice included."
        canonical="/grades/grade-11"
        keywords="Grade 11 Algebra 2, Trigonometry, NY Regents Algebra 2, complex numbers, logarithms, polynomial functions, 11th grade math"
        jsonLd={jsonLd}
      />
      {/* Navigation Bar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/grades/hub')}
              className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Back to Grades</span>
            </button>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/grades/grade-9')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 9
              </button>
              <button
                onClick={() => navigate('/grades/grade-10')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 10
              </button>
              <div className="px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 rounded-lg shadow-md whitespace-nowrap">
                Grade 11
              </div>
              <button
                onClick={() => navigate('/grades/grade-12')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 12
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-violet-300 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <i className="ri-graduation-cap-line text-2xl"></i>
            <span className="font-semibold text-sm">High School Mathematics</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Grade 11: Algebra 2 & Trigonometry
          </h1>
          <p className="text-xl text-indigo-100 max-w-3xl mx-auto leading-relaxed">
            Master advanced algebra, trigonometric functions, complex numbers, and analytical reasoning with comprehensive lessons and authentic practice exams
          </p>
        </div>
      </div>

      {/* Practice Test Banners */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-10">
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* State Tests Banner */}
          <button
            onClick={() => navigate('/grades/algebra2-tests')}
            className="group bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <i className="ri-map-pin-line text-3xl"></i>
              </div>
              <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform"></i>
            </div>
            <h3 className="text-2xl font-bold mb-2">State Practice Tests</h3>
            <p className="text-emerald-50 text-sm leading-relaxed">
              5 complete state exams: NY, OH, TX, VA, PA — 150 questions total
            </p>
          </button>

          {/* NY Regents Practice Tests Banner */}
          <button
            onClick={() => navigate('/grades/algebra2-regents')}
            className="group bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <i className="ri-file-list-3-line text-3xl"></i>
              </div>
              <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform"></i>
            </div>
            <h3 className="text-2xl font-bold mb-2">NY Regents Practice</h3>
            <p className="text-indigo-50 text-sm leading-relaxed">
              5 full Regents exams — 150 authentic questions with solutions
            </p>
          </button>

          {/* Full Open-Ended Regents Exam Banner */}
          <button
            onClick={() => navigate('/grades/algebra2-regents-open')}
            className="group bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <i className="ri-edit-box-line text-3xl"></i>
              </div>
              <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform"></i>
            </div>
            <h3 className="text-2xl font-bold mb-2">Full Regents Exam</h3>
            <p className="text-rose-50 text-sm leading-relaxed">
              Complete 4-part exam: Parts I–IV with open-ended questions
            </p>
          </button>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Worksheets Preview Banner */}
        <div className="mb-8 bg-gradient-to-r from-rose-500 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <h2 className="text-2xl font-bold">Practice Worksheets Available</h2>
              </div>
              <p className="text-rose-50 text-lg">
                Reinforce your Algebra II skills with our comprehensive worksheet collection
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBrowseAllClick}
                className="px-6 py-3 bg-white text-rose-600 rounded-xl font-semibold hover:bg-rose-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
              >
                Browse All Grade 11 Worksheets
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8 border-b-2 border-gray-200">
          <button
            onClick={() => setActiveTab('lessons')}
            className={`px-6 py-3 font-semibold transition-all duration-300 border-b-4 whitespace-nowrap ${
              activeTab === 'lessons'
                ? 'border-rose-500 text-rose-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-book-open-line mr-2"></i>
            Lessons
          </button>
          <button
            onClick={() => setActiveTab('worksheets')}
            className={`px-6 py-3 font-semibold transition-all duration-300 border-b-4 whitespace-nowrap ${
              activeTab === 'worksheets'
                ? 'border-rose-500 text-rose-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-file-list-3-line mr-2"></i>
            Worksheets
          </button>
        </div>

        {/* Lessons Tab */}
        {activeTab === 'lessons' && (
          <div className="space-y-8">
            <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-2xl p-8 mb-12 border border-indigo-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <i className="ri-user-smile-line text-3xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">
                    Welcome to Algebra 2 & Trigonometry!
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    This year, you'll explore the beauty of advanced functions, master trigonometric identities, work with complex numbers, and develop sophisticated problem-solving skills. Each lesson includes detailed explanations, worked examples, and 30 practice questions with complete step-by-step solutions.
                  </p>
                  <p className="text-slate-600 text-sm italic">
                    — Mr. Augustine, Mathematics Department
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {allLessons.map((lesson) => (
                <LessonCard key={lesson.id} lesson={lesson} />
              ))}
            </div>
          </div>
        )}

        {/* Worksheets Tab */}
        {activeTab === 'worksheets' && (
          <div className="space-y-8">
            {/* Free Sample Card */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-200 shadow-lg">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-emerald-500 text-white text-sm font-bold rounded-full">
                      FREE SAMPLE
                    </span>
                    <span className="px-3 py-1 bg-white text-emerald-700 text-sm font-semibold rounded-lg border border-emerald-200">
                      Medium
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {worksheets[0].title}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {worksheets[0].description}
                  </p>
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <span className="flex items-center gap-2">
                      <i className="ri-file-text-line text-emerald-600"></i>
                      {worksheets[0].pages} pages
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-calculator-line text-emerald-600"></i>
                      {worksheets[0].problems} problems
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleWorksheetClick(worksheets[0])}
                  className="px-6 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-download-line"></i>
                  Download Free Sample
                </button>
              </div>
            </div>

            {/* All Worksheets Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {worksheets.map((worksheet) => (
                <div
                  key={worksheet.id}
                  className={`bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
                    worksheet.isFree ? 'border-emerald-200' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {worksheet.isFree && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                          FREE
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                        worksheet.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                        worksheet.difficulty === 'Medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {worksheet.difficulty}
                      </span>
                    </div>
                    {!worksheet.isFree && (
                      <i className="ri-lock-line text-gray-400 text-xl"></i>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {worksheet.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {worksheet.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-file-text-line"></i>
                        {worksheet.pages}p
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calculator-line"></i>
                        {worksheet.problems}
                      </span>
                    </div>
                    <button
                      onClick={() => handleWorksheetClick(worksheet)}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                        worksheet.isFree
                          ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {worksheet.isFree ? 'Download' : 'Unlock'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Membership Upsell Section */}
            {showMembershipUpsell && (
              <div id="membership-upsell" className="bg-gradient-to-br from-rose-500 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
                <div className="text-center max-w-3xl mx-auto">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                    <i className="ri-vip-crown-line text-4xl"></i>
                  </div>
                  <h2 className="text-3xl font-bold mb-4">
                    Unlock All Grade 11 Worksheets
                  </h2>
                  <p className="text-rose-50 text-lg mb-6">
                    Get instant access to our complete collection of Algebra II practice worksheets, covering every topic from polynomials to sequences and series
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 mb-6">
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                      <i className="ri-check-line text-emerald-300"></i>
                      <span>150+ Practice Problems</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                      <i className="ri-check-line text-emerald-300"></i>
                      <span>Step-by-Step Solutions</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                      <i className="ri-check-line text-emerald-300"></i>
                      <span>Printable PDFs</span>
                    </div>
                  </div>
                  <button
                    onClick={handleBrowseAllClick}
                    className="px-8 py-4 bg-white text-rose-600 rounded-xl font-bold text-lg hover:bg-rose-50 transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap"
                  >
                    View All Worksheets & Plans
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}